import { useEffect, useRef } from 'react';

/** Pointer / keyboard activity on a demo anchor the engine decides about. */
const ACTIVATION_EVENTS = ['pointerdown', 'mousedown', 'click', 'keydown'];

/** Keys that keep their meaning while the tour is read-only: focus moves, menu navigation, closing. */
const PASSTHROUGH_KEYS = new Set([
    'Tab',
    'Escape',
    'ArrowUp',
    'ArrowDown',
    'ArrowLeft',
    'ArrowRight',
    'Home',
    'End',
    'PageUp',
    'PageDown',
]);
const MODIFIER_KEYS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Fn', 'NumLock', 'ScrollLock']);
const isModifierShortcut = (event) => event.ctrlKey || event.metaKey || event.altKey;

/**
 * A key press on the highlighted element that stands in for a click: any key
 * except focus / navigation keys, lone modifiers and OS / browser shortcuts.
 * Typing a task name therefore fills the reserved one instead of the letters.
 */
export const isActivationKey = (event) =>
    !PASSTHROUGH_KEYS.has(event.key) && !MODIFIER_KEYS.has(event.key) && !isModifierShortcut(event);

/** Keys that only move around an option list; anything else typed into it (search text, Enter on another option) is swallowed. */
const isNavigationKey = (event) => PASSTHROUGH_KEYS.has(event.key) || MODIFIER_KEYS.has(event.key);

const queryAll = (selector) => {
    try {
        return Array.from(document.querySelectorAll(selector));
    } catch {
        return [];
    }
};

const swallow = (event) => {
    event.preventDefault();
    event.stopPropagation();
};

/**
 * Read-only demo: decide what the user's activity on the current mark's
 * anchor does, per the step's `activation`.
 *
 * - default — the anchor itself never receives the activation: clicks and
 *   key presses (`isActivationKey`) on it are swallowed and turned into the
 *   step's auto-fill (`onActivate`). Keys that reach the anchor from
 *   elsewhere are untouched: the listener sits on the anchor, so only a key
 *   press that lands on the highlighted element counts.
 * - `'option'` — the anchor and the panels it opens (`observe`) are frozen
 *   except for the hinted option: clicking it lets the host's own handler run
 *   and then completes the step (`onOptionPicked`); every other click in
 *   there is swallowed, and so is every key but the navigation ones (Tab,
 *   Escape, arrows) — no search text, no Enter on some other option.
 * - `'passthrough'` — nothing is intercepted (native drag & drop); the
 *   step's completion predicate decides.
 *
 * Inactive without `enabled`, an `element` or a `step`.
 */
export function useDemoActivation({ enabled, element, step, hintElement, onActivate, onOptionPicked }) {
    const latest = useRef({ hintElement, onActivate, onOptionPicked });
    latest.current = { hintElement, onActivate, onOptionPicked };
    const activation = step?.activation;
    const observeKey = Array.isArray(step?.observe) ? step.observe.join(',') : '';

    useEffect(() => {
        if (!enabled || !element || !step || activation === 'passthrough') return undefined;

        if (activation === 'option') {
            const panels = () => (observeKey ? observeKey.split(',').flatMap(queryAll) : []);
            const inside = (target) =>
                target instanceof Node && (element.contains(target) || panels().some((panel) => panel.contains(target)));
            const onHint = (target) => {
                const hint = latest.current.hintElement;
                return Boolean(hint && target instanceof Node && hint.contains(target));
            };
            const guard = (event) => {
                if (!inside(event.target)) return;
                if (event.type === 'keydown') {
                    if (!isNavigationKey(event)) swallow(event);
                    return;
                }
                if (!onHint(event.target)) swallow(event);
            };
            // Bubble phase: the host's own click handler (select the option,
            // patch the form) has already run by the time the step completes.
            const picked = (event) => {
                if (onHint(event.target)) latest.current.onOptionPicked?.(event);
            };
            ACTIVATION_EVENTS.forEach((type) => document.addEventListener(type, guard, true));
            document.addEventListener('click', picked);
            return () => {
                ACTIVATION_EVENTS.forEach((type) => document.removeEventListener(type, guard, true));
                document.removeEventListener('click', picked);
            };
        }

        const activate = (event) => {
            if (event.type === 'keydown' && !isActivationKey(event)) return;
            swallow(event);
            if (event.type === 'click' || event.type === 'keydown') latest.current.onActivate?.(event);
        };
        ACTIVATION_EVENTS.forEach((type) => element.addEventListener(type, activate, true));
        return () => ACTIVATION_EVENTS.forEach((type) => element.removeEventListener(type, activate, true));
    }, [enabled, element, step, activation, observeKey]);
}

export default useDemoActivation;
